/**
 * countdownUtil.ts — one source of truth for what a ⏱️ countdown block counts
 * down to, shared by every renderer (builder canvas, preview modal, public
 * campaign page, public landing/form page) so all four agree.
 *
 * Two modes, selected by `props.source`:
 *   'manual' (default, and what EVERY existing block in the DB has — the prop is
 *            absent there) → counts to the hand-typed `props.targetDate`.
 *   'campaignStart'        → counts to the campaign's optional launch time
 *                            (Campaign.startDate), then automatically continues
 *                            to Campaign.endDate until the campaign closes.
 *
 * Absent `source` MUST behave as 'manual' — never infer 'campaignStart' from
 * the presence of a campaign, or every legacy countdown would change meaning.
 */

export type CountdownSource = 'manual' | 'campaignStart';

/** What the surrounding page knows about the campaign, when anything at all. */
export interface CountdownContext {
  /** Campaign.startDate — ISO string / Date / undefined when never set. */
  campaignStartDate?: string | Date | null;
  /** Campaign.endDate — used after the launch countdown reaches zero. */
  campaignEndDate?: string | Date | null;
  /**
   * Editor flag. In the builder we never hide the block, otherwise it would
   * vanish from under the designer's cursor the moment the campaign goes live
   * (and three separate height-measuring passes would record it as 0px high).
   */
  isEditor?: boolean;
}

export interface CountdownResolution {
  /** What to count down to. null when there is nothing to count to. */
  target: Date | null;
  /**
   * 'manual'    — ordinary countdown to a typed date.
   * 'preLaunch' — counting down to the campaign's launch.
   * 'campaignEnd' — campaign is live and the same timer now counts to closing.
   * 'hidden'    — public pages should render nothing (campaign already closed,
   *               or campaignStart mode with no start date configured).
   * 'placeholder' — editor-only: campaignStart mode, but this canvas has no
   *               start date to preview, so show dashes instead of a wrong time.
   */
  mode: 'manual' | 'preLaunch' | 'campaignEnd' | 'hidden' | 'placeholder';
}

const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(String(v));
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Resolve a countdown block's target. `props` is the merged props object the
 * renderer already computed (desktop props + mobile overrides).
 */
export function resolveCountdown(
  props: { source?: CountdownSource; targetDate?: string },
  ctx: CountdownContext = {},
): CountdownResolution {
  const source: CountdownSource = props?.source === 'campaignStart' ? 'campaignStart' : 'manual';

  if (source === 'manual') {
    // Legacy behavior, byte-for-byte: a missing targetDate falls back to
    // "3 days from now" so a freshly dropped block shows something ticking.
    const typed = toDate(props?.targetDate);
    return {
      target: typed || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
      mode: 'manual',
    };
  }

  const start = toDate(ctx.campaignStartDate);
  if (!start) {
    // Mode is on but the campaign has no launch time. Nothing to promise on a
    // public page; in the editor show a placeholder so the designer can still
    // position and style the block.
    return { target: null, mode: ctx.isEditor ? 'placeholder' : 'hidden' };
  }
  if (start.getTime() <= Date.now()) {
    const end = toDate(ctx.campaignEndDate);
    if (end && end.getTime() > Date.now()) {
      return { target: end, mode: 'campaignEnd' };
    }
    // Preserve the old hide-after-launch behaviour when there is no closing
    // date. Once a configured closing date passes, public pages hide it too.
    return { target: end || start, mode: ctx.isEditor && end ? 'campaignEnd' : ctx.isEditor ? 'preLaunch' : 'hidden' };
  }
  return { target: start, mode: 'preLaunch' };
}

/** Split a remaining-time span into padded d/h/m/s strings. */
export function countdownParts(target: Date | null, now: number = Date.now()) {
  const diff = target ? Math.max(0, target.getTime() - now) : 0;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    mins: Math.floor((diff % 3600000) / 60000),
    secs: Math.floor((diff % 60000) / 1000),
  };
}

/** Default heading shown above the boxes in pre-launch mode. */
export const PRE_LAUNCH_LABEL = 'הקמפיין מתחיל בעוד';

/** Heading shown after launch, while the same timer counts to closing. */
export const CAMPAIGN_END_LABEL = 'הקמפיין נסגר בעוד';

/**
 * `datetime-local` inputs speak LOCAL time while `toISOString()` speaks UTC —
 * formatting a 20:00 Israel launch with toISOString().slice(0,16) hydrates the
 * input as 17:00. Use these two helpers on both ends of such an input.
 */
export function toDatetimeLocalValue(v?: string | Date | null): string {
  const d = toDate(v);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** '' → '' (clears the field); otherwise an ISO string the backend can cast. */
export function fromDatetimeLocalValue(v: string): string {
  if (!v) return '';
  const d = new Date(v); // parsed as local time, which is what the user typed
  return isNaN(d.getTime()) ? '' : d.toISOString();
}
